import { router, usePage } from "@inertiajs/react";
import { useState } from "react";

import CourseGrid from "@/components/CourseGrid";
import SearchableSelect from "@/components/SearchableSelect";
import { monthFilterValue, monthNumber, monthOptions } from "@/lib/months";
import { courses as coursesRoutes } from "@/routes";
import type { Course, SharedProps } from "@/types";

const PILL_CLASSES =
  "flex h-11 items-center rounded-full border border-ink/30 bg-white px-5 font-sans text-sm font-light text-ink";

type StudentFilters = {
  category: string;
  month: string;
  house: string;
  course_type: string;
};

function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block font-sans text-xs font-medium uppercase tracking-wide text-ink/50">
      {children}
    </span>
  );
}

type Props = {
  data: Course[];
  filters: StudentFilters;
  categories: string[];
  houses: string[];
};

export default function StudentIndex({
  data: upcoming,
  filters,
  categories,
  houses,
}: Props) {
  const { currentUser } = usePage<SharedProps>().props;
  const [local, setLocal] = useState<StudentFilters>({
    ...filters,
    month: monthNumber(filters.month),
  });
  const hasActiveFilters =
    local.category !== "" ||
    local.month !== "" ||
    local.house !== "" ||
    local.course_type !== "";

  // course_type only arrives via the landing page's type cards, with no
  // dedicated selector here — once the user searches with the visible
  // filters, drop it rather than keep it silently applied underneath.
  function search() {
    setLocal({ ...local, course_type: "" });
    router.get(
      coursesRoutes.index().url,
      { ...local, month: monthFilterValue(local.month), course_type: "" },
      { preserveScroll: true },
    );
  }

  function clearFilters() {
    const cleared = { category: "", month: "", house: "", course_type: "" };
    setLocal(cleared);
    router.get(coursesRoutes.index().url, cleared, { preserveScroll: true });
  }

  return (
    <div className="mx-auto w-[92%] max-w-[90rem] py-10">
      <div className="text-center">
        <h1 className="font-sans text-4xl font-semibold text-ink sm:text-5xl">
          ¡Hola {currentUser?.first_name ?? currentUser?.full_name}!
        </h1>
        <p className="mt-3 font-sans text-lg font-light text-ink/80">
          ¡Aquí puedes revisar los cursos que tenemos para ti!
        </p>
      </div>

      <form
        className="mt-4 flex flex-wrap items-start justify-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <div className="w-full sm:w-52">
          <FilterLabel>Tipos de curso</FilterLabel>
          <div className={PILL_CLASSES}>
            <SearchableSelect
              value={local.category}
              onChange={(category) => setLocal({ ...local, category })}
              options={[
                { label: "Todos", value: "" },
                ...categories.map((name) => ({ label: name, value: name })),
              ]}
            />
          </div>
        </div>
        <div className="w-full sm:w-52">
          <FilterLabel>Mes de realización</FilterLabel>
          <div className={PILL_CLASSES}>
            <SearchableSelect
              value={local.month}
              onChange={(month) => setLocal({ ...local, month })}
              options={monthOptions()}
            />
          </div>
        </div>
        <div className="w-full sm:w-52">
          <FilterLabel>Casa</FilterLabel>
          <div className={PILL_CLASSES}>
            <SearchableSelect
              value={local.house}
              onChange={(house) => setLocal({ ...local, house })}
              options={[
                { label: "Todas", value: "" },
                ...houses.map((name) => ({ label: name, value: name })),
              ]}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 self-end">
          <button
            type="submit"
            aria-label="Buscar"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/30 bg-white text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <SearchIcon className="h-4 w-4" />
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="self-center font-sans text-xs text-ink/50 underline underline-offset-2 transition-colors hover:text-ink"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </form>

      <section className="mt-10">
        <h2 className="mb-5 font-sans text-xl font-semibold text-ink">
          Próximos cursos para ti:
        </h2>
        <CourseGrid
          courses={upcoming}
          emptyMessage="No hay cursos disponibles por el momento."
        />
      </section>
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-4.35-4.35M17 10.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
      />
    </svg>
  );
}
